type WhitelabelFeatureFlags = {
  recurrencyEnabled: boolean | null
  cardsEnabled: boolean | null
  terminalsEnabled: boolean | null
  insuranceEnabled: boolean | null
}

export type RuntimeWhitelabelResponse = {
  whitelabel: {
    id: string
    tag: string
    name: string
    active: boolean
  }
  configs: {
    support: string | null
    supportWhatsapp: string | null
  }
  featureFlags: WhitelabelFeatureFlags
}

const whitelabels = {
  divipay: {
    logo: '/divipay.png',
    recurrencyEnabled: true,
    cardsEnabled: true,
    terminalsEnabled: true,
    insuranceEnabled: false,
    logo_inverted: '/divipay-inverted.png',
    favicon: '/divipay.ico',
    banner: '/divipay-banner.png',
    name: 'DiviPay',
    site: 'https://divipay.com.br',
    support: 'mailto:atendimento@divipay.com.br',
    docs: 'https://api.divipay.com.br/docs',
    supportWhatsapp: 'https://api.whatsapp.com/send?phone=5541999875426&text=Ol%C3%A1%2C%20preciso%20de%20uma%20ajuda.',
    brand: {
      100: '#ffe3b3',
      200: '#ffd181',
      300: '#ffbe4d',
      400: '#ffaf23',
      500: '#ffa100',
      600: '#fc9500',
      700: '#f78500',
      800: '#f17500',
      900: '#d18502',
    },
  },
  mov: {
    logo: '/mov.png',
    recurrencyEnabled: true,
    cardsEnabled: false,
    terminalsEnabled: true,
    insuranceEnabled: false,
    logo_inverted: '/mov-inverted.png',
    favicon: '/mov.ico',
    banner: '/mov-banner.png',
    name: 'MovPay',
    site: 'https://movpay.com.br',
    support: 'mailto:central@movpay.com.br',
    docs: 'https://api.divipay.com.br/docs',
    supportWhatsapp: 'https://api.whatsapp.com/send?phone=5549998134577&text=Ol%C3%A1%2C%20preciso%20de%20uma%20ajuda.',
    brand: {
      '100': '#e1e3f1',
      '200': '#b3b9db',
      '300': '#858ec5',
      '400': '#5763af',
      '500': '#2a3899',
      '600': '#1f2b7a',
      '700': '#151e5b',
      '800': '#0a113c',
      '900': '#010421',
    },
  },
  reality: {
    logo: '/reality.png',
    recurrencyEnabled: true,
    cardsEnabled: true,
    terminalsEnabled: true,
    insuranceEnabled: false,
    logo_inverted: '/reality-inverted.png',
    favicon: '/reality.ico',
    banner: '/reality-banner.png',
    name: 'Reality',
    site: 'https://realitycard.com.br',
    support: 'mailto:contato@realitycard.com.br',
    docs: 'https://api.divipay.com.br/docs',
    supportWhatsapp: 'https://api.whatsapp.com/send?phone=5531984164374&text=Ol%C3%A1%2C%20preciso%20de%20uma%20ajuda.',
    brand: {
      100: '#ffc8b8',
      200: '#ffa38a',
      300: '#ff7b5b',
      400: '#ff5736',
      500: '#ff280c',
      600: '#ff1f07',
      700: '#ff1100',
      800: '#f10000',
      900: '#da0000',
    },
  },
  hger: {
    recurrencyEnabled: true,
    cardsEnabled: false,
    terminalsEnabled: false,
    insuranceEnabled: false,
    logo: '/hger.png',
    logo_inverted: '/hger-white.png',
    favicon: '/hger.ico',
    banner: '/banner-hger.svg',
    name: 'Hger Pay',
    site: 'https://hger.com.br',
    support: 'mailto:atendimento@hger.com.br',
    docs: 'https://api.hger.com.br/docs',
    supportWhatsapp: 'https://api.whatsapp.com/send?phone=49999464580',
    brand: {
      100: '#caf0f8',
      200: '#ade8f4',
      300: '#90e0ef',
      400: '#48cae4',
      500: '#00b4d8',
      600: '#0096c7',
      700: '#0077b6',
      800: '#023e8a',
      900: '#03045e',
    },
  },
  sigma: {
    recurrencyEnabled: true,
    cardsEnabled: true,
    terminalsEnabled: true,
    insuranceEnabled: false,
    logo: '/sigma.png',
    logo_inverted: '/sigma-inverted.png',
    favicon: '/sigma.ico',
    banner: '/sigma-banner.png',
    name: 'Sigma',
    site: 'https://sigmapaysistema.com',
    support: 'mailto:atendimento@sigmapaysistema.com',
    docs: 'https://api.divipay.com.br/docs',
    supportWhatsapp: 'https://api.whatsapp.com/send?phone=55&text=Ol%C3%A1%2C%20preciso%20de%20uma%20ajuda.',
    brand: {
      100: '#e8f1fc',
      200: '#c5d9f6',
      300: '#a1c1f0',
      400: '#7ea9ea',
      500: '#6198e0',
      600: '#4d7bc0',
      700: '#3a5e9f',
      800: '#27427f',
      900: '#14265e',
    },
  },
}

type WhitelabelTag = keyof typeof whitelabels
type WhitelabelConfig = (typeof whitelabels)[WhitelabelTag]

const runtimeOverrides: Partial<Record<WhitelabelTag, Partial<WhitelabelConfig>>> = {}

export const getWhitelabelTag = () => process.env.REACT_APP_WHITELABEL as WhitelabelTag

const extractWhatsappPhone = (value: string) => {
  const match = value.match(/[?&]phone=([^&]+)/)

  if (match?.[1]) {
    return decodeURIComponent(match[1]).replace(/\D/g, '')
  }

  return value.replace(/\D/g, '')
}

export const buildWhatsappLink = (phone: string) => {
  const normalizedPhone = extractWhatsappPhone(phone)
  return `https://api.whatsapp.com/send?phone=${normalizedPhone}`
}

export const formatWhatsappPhone = (phone?: string | null) => {
  if (!phone) {
    return ''
  }

  const digits = extractWhatsappPhone(phone)

  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`
  }

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }

  return phone
}

export const hydrateRuntimeWhitelabel = (config?: RuntimeWhitelabelResponse | null) => {
  const tag = getWhitelabelTag()

  if (!tag || !whitelabels[tag] || !config) {
    return
  }

  runtimeOverrides[tag] = {
    support: config.configs.support ? `mailto:${config.configs.support}` : undefined,
    supportWhatsapp: config.configs.supportWhatsapp
      ? buildWhatsappLink(config.configs.supportWhatsapp)
      : undefined,
    recurrencyEnabled: config.featureFlags.recurrencyEnabled ?? false,
    cardsEnabled: config.featureFlags.cardsEnabled ?? false,
    terminalsEnabled: config.featureFlags.terminalsEnabled ?? false,
    insuranceEnabled: config.featureFlags.insuranceEnabled ?? false,
  }
}

export const getCurrentWhitelabelConfig = () => {
  const tag = getWhitelabelTag()

  if (!tag) {
    return undefined
  }

  return {
    ...whitelabels[tag],
    ...runtimeOverrides[tag],
  }
}

export const getVariable = (key: string) => {
  return getCurrentWhitelabelConfig()?.[key]
}
